/* import __COLOCATED_TEMPLATE__ from './content-search.hbs'; */
import { A } from '@ember/array';
import { equal } from '@ember/object/computed';
import EmberObject, { computed, observer } from '@ember/object';
import Component from '@ember/component';
import { ADMIN_CONTENT_SEARCH } from 'sh-www/constants/routes/admin/content-search';
import { on } from '@ember/object/evented';
import { CLASSIFICATION_SECTIONS } from 'sh-www/models/classification';

/**
 * Known issue: The location fields do not currently support being pre-filled since it is a type-
 * ahead component. The best solution is likely to change it so that it accepts only the ID of the
 * location and only ever shows the formatted address after fetching it internally, which will need
 * to happen each time the ID changes.
 */

const {
  FILTERS,
  FILTERS_PROPERTY_NAMES,
  ON_PLATFORM_ONLY_FILTERS,
  FILTERS_OBJECT,
  NULL_OPTION_LABEL,
  NULL_OPTION,
  PLATFORM_STATUSES,
  PLATFORM_STATUS_LABELS,
  PLATFORM_STATUS_OPTIONS,
  SELECT_STATUS_LABELS,
  SELECT_STATUS_OPTIONS,
  HAS_LINK_LABELS,
  HAS_LINK_OPTIONS,
  HOST_LABELS,
  HOST_OPTIONS,
} = ADMIN_CONTENT_SEARCH;

const CLASS_NAME = 'SH-c-admin-content-search';

export default Component.extend({
  tagName: '',
  CLASS_NAME,
  FILTERS,
  PLATFORM_STATUS_OPTIONS,
  SELECT_STATUS_OPTIONS,
  HAS_LINK_OPTIONS,
  HOST_OPTIONS,
  NULL_OPTION_LABEL,

  /** @param {Ember.Service|null} */
  moment: null,

  /** @param {Function} */
  onAddToast: () => {},

  /** @param {A<ProjectDigest>} */
  projectDigests: A([]),

  /** @param {A<Country>} */
  availableCountries: A([]),

  /** @param {A<Service>} */
  availableServices: A([]),

  /** @param {A<VideoCategory>} */
  availableVideoCategories: A([]),

  /** @param {A<Classification>} */
  classificationItems: [],

  /** @param {Ember.Object|null} */
  filters: null,

  /** @param {String|null} */
  sortValue: null,

  /** @param {String|null} */
  orderValue: null,

  /** @param {A<Object>} */
  sortOptions: null,

  /** @param {Pagination|null} */
  pagination: null,

  /** @param {Boolean} */
  isClean: false,

  /** @param {Boolean} */
  isLoading: false,

  /** @param {Boolean} */
  isSearching: false,

  /** @param {Function} */
  onSetFilters: () => {},

  /** @param {Function} */
  onPageChange: () => {},

  /** @param {Function} */
  onGetThumbnail: () => {},

  /** @param {Function} */
  onOpenVideoPlayer: () => {},

  /** @param {Function} */
  setOrderValue: () => {},

  /** @param {Function} */
  setSortValue: () => {},

  /** @param {Function} */
  setClassificationsChanged: () => {},

  /** @param {Function} */
  setFeaturedChanged: () => {},

  /** @type {Ember.Object} */
  _filters: EmberObject.create(FILTERS_OBJECT),

  /** @type {Boolean} */
  _isClean: computed(`_filters.{${FILTERS_PROPERTY_NAMES.join(',')}}`, function () {
    const _filters = this.get('_filters');
    return Object.keys(_filters).every((filter) => _filters.get(filter) === null);
  }),

  /** @type {Boolean} */
  noResults: computed('isClean', 'projectDigests.[]', function () {
    return !this.get('isClean') && !this.get('projectDigests.length');
  }),

  /** @type {Boolean} */
  showPagination: computed('isSearching', 'projectDigests.[]', function () {
    return !this.get('isSearching') && this.get('projectDigests.length');
  }),

  /** @type {String} */
  platformStatusLabel: computed(`_filters.${FILTERS.PLATFORM_STATUS}`, function () {
    return PLATFORM_STATUS_LABELS[this.get(`_filters.${FILTERS.PLATFORM_STATUS}`)]
      || NULL_OPTION_LABEL;
  }),

  /** @type {String} */
  selectStatusLabel: computed(`_filters.${FILTERS.SELECT_STATUS}`, function () {
    return SELECT_STATUS_LABELS[this.get(`_filters.${FILTERS.SELECT_STATUS}`)]
      || NULL_OPTION_LABEL;
  }),

  /** @type {String} */
  hasLinkLabel: computed(`_filters.${FILTERS.HAS_LINK}`, function () {
    return HAS_LINK_LABELS[this.get(`_filters.${FILTERS.HAS_LINK}`)]
      || NULL_OPTION_LABEL;
  }),

  /** @type {String} */
  hostLabel: computed(`_filters.${FILTERS.HOST}`, function () {
    return HOST_LABELS[this.get(`_filters.${FILTERS.HOST}`)]
      || NULL_OPTION_LABEL;
  }),

  /** @type {Object} */
  freelancerCountryLabels: computed('availableCountries.[]', function () {
    return this.get('availableCountries').reduce((object, country) => {
      object[country.get('code')] = country.get('name');
      return object;
    }, {});
  }),

  /** @type {Array<Object>} */
  freelancerCountryOptions: computed('availableCountries.[]', function () {
    return [
      NULL_OPTION,
      ...this.get('availableCountries').map((country) => ({
        key: country.get('name'),
        model: country.get('code'),
      })),
    ];
  }),

  /** @type {String} */
  freelancerCountryLabel: computed(
    `_filters.${FILTERS.FREELANCER_COUNTRY}`,
    'freelancerCountryLabels',
    function () {
      return this.get('freelancerCountryLabels')[this.get(`_filters.${FILTERS.FREELANCER_COUNTRY}`)]
        || NULL_OPTION_LABEL;
    },
  ),

  /** @type {A<Object>} */
  selectedServices: computed(
    `_filters.${FILTERS.SERVICES_ANY}`,
    `_filters.${FILTERS.SERVICES_ALL}`,
    'servicesMatchAll',
    'availableServices.[]',
    function () {
      const selectedServices = this.get('servicesMatchAll')
        ? this.get(`_filters.${FILTERS.SERVICES_ALL}`)
        : this.get(`_filters.${FILTERS.SERVICES_ANY}`);

      if (!selectedServices) {
        return A([]);
      }

      const availableServicesMap = this.get('availableServices').reduce((accum, model) => {
        accum[model.get('id')] = model;
        return accum;
      }, { });

      return selectedServices.split(',').reduce((accum, serviceId) => {
        const service = availableServicesMap[serviceId];

        if (service) {
          accum.push(service);
        }

        return accum;
      }, []);
    },
  ),

  /** @type {Boolean} */
  servicesMatchAll: false,

  /** @type {A<Object>} */
  selectedVideoTopics: computed(
    `_filters.${FILTERS.VIDEO_TOPICS_ANY}`,
    `_filters.${FILTERS.VIDEO_TOPICS_ALL}`,
    'videoTopicsMatchAll',
    function () {
      let selectedVideoTopics = this.get('videoTopicsMatchAll')
        ? this.get(`_filters.${FILTERS.VIDEO_TOPICS_ALL}`)
        : this.get(`_filters.${FILTERS.VIDEO_TOPICS_ANY}`);

      if (selectedVideoTopics === null) {
        return A([]);
      }

      return A(selectedVideoTopics.split(','));
    },
  ),

  /** @type {Boolean} */
  videoTopicsMatchAll: false,

  /** @type {A<Object>} */
  selectedVideoCategories: computed(
    `_filters.${FILTERS.VIDEO_CATEGORIES}`,
    'availableVideoCategories.[]',
    function () {
      let selectedCategories = this.get(`_filters.${FILTERS.VIDEO_CATEGORIES}`);

      if (selectedCategories === null) {
        return A([]);
      }

      selectedCategories = selectedCategories.split(',');

      return this.get('availableVideoCategories').filter((category) => {
        return selectedCategories.includes(category.get('id'));
      });
    },
  ),

  /** @type {A<Classification>} */
  subjectClassificationOptions: computed(`classificationItems.${CLASSIFICATION_SECTIONS.SUBJECT}.[]`, function () {
    return this.classificationItems[CLASSIFICATION_SECTIONS.SUBJECT];
  }),

  /** @type {A<Classification>} */
  mediumClassificationOptions: computed(`classificationItems.${CLASSIFICATION_SECTIONS.MEDIUM}.[]`, function () {
    return this.classificationItems[CLASSIFICATION_SECTIONS.MEDIUM];
  }),

  /** @type {A<Classification>} */
  industryClassificationOptions: computed(`classificationItems.${CLASSIFICATION_SECTIONS.INDUSTRY}.[]`, function () {
    return this.classificationItems[CLASSIFICATION_SECTIONS.INDUSTRY];
  }),

  /** @type {A<Classification>} */
  selectedSubjectClassifications: computed(
    `_filters.${FILTERS.SUBJECTS}`,
    'subjectClassificationOptions.@each.id',
    function () {
      const selectedSubjects = this._filters[FILTERS.SUBJECTS];
      const selectedSubjectsSet = new Set(selectedSubjects ? selectedSubjects.split(',') : []);
      return this.subjectClassificationOptions.filter(classification => {
        return selectedSubjectsSet.has(classification.id);
      });
    },
  ),

  /** @type {A<Classification>} */
  selectedMediumClassifications: computed(
    `_filters.${FILTERS.MEDIUMS}`,
    'subjectClassificationOptions.@each.id',
    function () {
      const selectedMediums = this._filters[FILTERS.MEDIUMS];
      const selectedMediumsSet = new Set(selectedMediums ? selectedMediums.split(',') : []);
      return this.mediumClassificationOptions.filter(classification => {
        return selectedMediumsSet.has(classification.id);
      });
    },
  ),

  /** @type {A<Classification>} */
  selectedIndustryClassifications: computed(
    `_filters.${FILTERS.INDUSTRIES}`,
    'subjectClassificationOptions.@each.id',
    function () {
      const selectedIndustries = this._filters[FILTERS.INDUSTRIES];
      const selectedIndustriesSet = new Set(selectedIndustries ? selectedIndustries.split(',') : []);
      return this.industryClassificationOptions.filter(classification => {
        return selectedIndustriesSet.has(classification.id);
      });
    },
  ),

  /** @type {String} */
  locationLabel: '',

  /** @type {String} */
  locationValue: computed(`_filters.${FILTERS.LOCATION}`, 'locationLabel', function () {
    const id = this.get(`_filters.${FILTERS.LOCATION}`);
    return id ? this.get('locationLabel') : '';
  }),

  /** @type {String} */
  freelancerLocationLabel: '',

  /** @type {String} */
  freelancerLocationValue: computed(
    `_filters.${FILTERS.FREELANCER_LOCATION}`,
    'freelancerLocationLabel',
    function () {
      const id = this.get(`_filters.${FILTERS.FREELANCER_LOCATION}`);
      return id ? this.get('freelancerLocationLabel') : '';
    },
  ),

  /** @type {Boolean} */
  onPlatform: equal(`_filters.${FILTERS.PLATFORM_STATUS}`, PLATFORM_STATUSES.ON_PLATFORM),

  filtersObserver: on('init', observer('filters', function () {
    FILTERS_PROPERTY_NAMES.forEach((filter) => {
      this.set(`_filters.${filter}`, this.get(`filters.${filter}`));
    });
  })),

  /** @type {Boolean} */
  selectFreelancersOnly: false,

  /** @type {Boolean} */
  featuredOnly: false,

  /** @type {Boolean} */
  untaggedOnly: false,

  actions: {
    handleInput(filter, value) {
      this.set(`_filters.${filter}`, value || null);
    },

    handleSelect(filter, value) {
      this.set(`_filters.${filter}`, value);
    },

    handlePlatformStatusSelect(value) {
      this.set(`_filters.${FILTERS.PLATFORM_STATUS}`, value);

      // If anything but on-platform is selected, unset the filters that only apply for on-platform.
      if (value !== PLATFORM_STATUSES.ON_PLATFORM) {
        ON_PLATFORM_ONLY_FILTERS.forEach((filter) => {
          this.set(`_filters.${filter}`, null);
        });
      }
    },

    /**
     * @param {Location|null} location
     */
    handleSelectLocation(location) {
      this.set(`_filters.${FILTERS.LOCATION}`, location ? location.place_id : null);
      this.set('locationLabel', location ? location.formatted_address : '');
    },

    /**
     * @param {Location|null} location
     */
    handleSelectFreelancerLocation(location) {
      this.set(`_filters.${FILTERS.FREELANCER_LOCATION}`, location ? location.place_id : null);
      this.set('freelancerLocationLabel', location ? location.formatted_address : '');
    },

    handleInputFreelancerId(value) {
      this.set(`_filters.${FILTERS.FREELANCER_ID}`, value || null);

      // Unset other freelancer values.
      this.setProperties({
        [`_filters.${FILTERS.FREELANCER_NAME}`]: null,
        [`_filters.${FILTERS.FREELANCER_EMAIL}`]: null,
        [`_filters.${FILTERS.FREELANCER_COUNTRY}`]: null,
        [`_filters.${FILTERS.FREELANCER_LOCATION}`]: null,
      });
    },

    handleInputFreelancerEmail(value) {
      this.set(`_filters.${FILTERS.FREELANCER_EMAIL}`, value || null);

      // Unset other freelancer values.
      this.setProperties({
        [`_filters.${FILTERS.FREELANCER_ID}`]: null,
        [`_filters.${FILTERS.FREELANCER_NAME}`]: null,
        [`_filters.${FILTERS.FREELANCER_COUNTRY}`]: null,
        [`_filters.${FILTERS.FREELANCER_LOCATION}`]: null,
      });
    },

    handleInputCompanyId(value) {
      this.set(`_filters.${FILTERS.COMPANY_ID}`, value || null);

      // Unset other company values.
      this.setProperties({
        [`_filters.${FILTERS.COMPANY_NAME}`]: null,
      });
    },

    /**
     * @param {Object} selection
     */
    handleAddService(selection) {
      const key = this.get('servicesMatchAll')
        ? `_filters.${FILTERS.SERVICES_ALL}`
        : `_filters.${FILTERS.SERVICES_ANY}`;

      const services = this.get(key);

      if (services === null) {
        this.set(key, selection.get('id'));
        return;
      }

      this.set(key, `${services},${selection.get('id')}`);
    },

    /**
     * @param {Object} selection
     */
    handleRemoveService(selection) {
      const key = this.get('servicesMatchAll')
        ? `_filters.${FILTERS.SERVICES_ALL}`
        : `_filters.${FILTERS.SERVICES_ANY}`;

      const services = this.get(key);
      const servicesArray = A(services.split(','));

      servicesArray.splice(servicesArray.indexOf(selection.get('id')), 1);

      this.set(key, servicesArray.join(','));

      if (!servicesArray.length) {
        this.set(key, null);
      }
    },

    handleToggleServicesMode() {
      const servicesMatchAll = this.get('servicesMatchAll');

      if (servicesMatchAll) {
        const servicesAll = this.get(`_filters.${FILTERS.SERVICES_ALL}`);
        this.set(`_filters.${FILTERS.SERVICES_ANY}`, servicesAll);
        this.set(`_filters.${FILTERS.SERVICES_ALL}`, null);
      } else {
        const servicesAny = this.get(`_filters.${FILTERS.SERVICES_ANY}`);
        this.set(`_filters.${FILTERS.SERVICES_ALL}`, servicesAny);
        this.set(`_filters.${FILTERS.SERVICES_ANY}`, null);
      }

      this.set('servicesMatchAll', !servicesMatchAll);
    },

    /**
     * @param {Object} selection
     */
    handleAddVideoTopic(selection) {
      const key = this.get('videoTopicsMatchAll')
        ? `_filters.${FILTERS.VIDEO_TOPICS_ALL}`
        : `_filters.${FILTERS.VIDEO_TOPICS_ANY}`;

      const videoTopics = this.get(key);

      if (videoTopics === null) {
        this.set(key, selection.get('id'));
        return;
      }

      this.set(key, `${videoTopics},${selection.get('id')}`);
    },

    /**
     * @param {Object} selection
     */
    handleRemoveVideoTopic(selection) {
      const key = this.get('videoTopicsMatchAll')
        ? `_filters.${FILTERS.VIDEO_TOPICS_ALL}`
        : `_filters.${FILTERS.VIDEO_TOPICS_ANY}`;

      const videoTopics = this.get(key);
      const videoTopicsArray = A(videoTopics.split(','));

      videoTopicsArray.splice(videoTopicsArray.indexOf(selection.get('id')), 1);

      this.set(key, videoTopicsArray.join(','));

      if (!videoTopicsArray.length) {
        this.set(key, null);
      }
    },

    handleToggleVideoTopicsMode() {
      const videoTopicsMatchAll = this.get('videoTopicsMatchAll');

      if (videoTopicsMatchAll) {
        const videoTopicsAll = this.get(`_filters.${FILTERS.VIDEO_TOPICS_ALL}`);
        this.set(`_filters.${FILTERS.VIDEO_TOPICS_ANY}`, videoTopicsAll);
        this.set(`_filters.${FILTERS.VIDEO_TOPICS_ALL}`, null);
      } else {
        const videoTopicsAny = this.get(`_filters.${FILTERS.VIDEO_TOPICS_ANY}`);
        this.set(`_filters.${FILTERS.VIDEO_TOPICS_ALL}`, videoTopicsAny);
        this.set(`_filters.${FILTERS.VIDEO_TOPICS_ANY}`, null);
      }

      this.set('videoTopicsMatchAll', !videoTopicsMatchAll);
    },

    handleToggleSelectFreelancers() {
      this.set('selectFreelancersOnly', !this.selectFreelancersOnly);

      // If toggle to false, remove filter
      if (!this.selectFreelancersOnly) {
        this.set(`_filters.${FILTERS.FREELANCER_SELECT}`, null);
      } else {
        this.set(`_filters.${FILTERS.FREELANCER_SELECT}`, this.selectFreelancersOnly);
      }
    },

    /**
     * @param {Object} selection
     */
    handleAddCategory(selection) {
      const key = `_filters.${FILTERS.VIDEO_CATEGORIES}`;
      const videoCategories = this.get(key);

      if (videoCategories === null) {
        this.set(key, selection.get('id'));
        return;
      }

      this.set(key, `${videoCategories},${selection.get('id')}`);
    },

    /**
     * @param {Object} selection
     */
    handleRemoveCategory(selection) {
      const key = `_filters.${FILTERS.VIDEO_CATEGORIES}`;
      const videoCategories = this.get(key);
      const videoCategoriesArray = A(videoCategories.split(','));

      videoCategoriesArray.splice(videoCategoriesArray.indexOf(selection.get('id')), 1);

      this.set(key, videoCategoriesArray.join(','));

      if (!videoCategoriesArray.length) {
        this.set(key, null);
      }
    },

    handleSubjectAdded(selection) {
      const key = `_filters.${FILTERS.SUBJECTS}`;
      const subjects = this._filters[FILTERS.SUBJECTS];

      if (subjects == null) {
        this.set(key, selection.id);
        return;
      }

      this.set(key, `${subjects},${selection.id}`);
    },

    handleSubjectRemoved(selection) {
      const subjects = this._filters[FILTERS.SUBJECTS];
      const subjectsArray = subjects.split(',');

      subjectsArray.splice(subjectsArray.indexOf(selection.id), 1);

      const key = `_filters.${FILTERS.SUBJECTS}`;
      this.set(key, subjectsArray.join(','));

      if (!subjectsArray.length) {
        this.set(key, null);
      }
    },

    handleMediumAdded(selection) {
      const key = `_filters.${FILTERS.MEDIUMS}`;
      const mediums = this._filters[FILTERS.MEDIUMS];

      if (mediums == null) {
        this.set(key, selection.id);
        return;
      }

      this.set(key, `${mediums},${selection.id}`);
    },

    handleMediumRemoved(selection) {
      const mediums = this._filters[FILTERS.MEDIUMS];
      const mediumsArray = mediums.split(',');

      mediumsArray.splice(mediumsArray.indexOf(selection.id), 1);

      const key = `_filters.${FILTERS.MEDIUMS}`;
      this.set(key, mediumsArray.join(','));

      if (!mediumsArray.length) {
        this.set(key, null);
      }
    },

    handleIndustryAdded(selection) {
      const key = `_filters.${FILTERS.INDUSTRIES}`;
      const industries = this._filters[FILTERS.INDUSTRIES];

      if (industries == null) {
        this.set(key, selection.id);
        return;
      }

      this.set(key, `${industries},${selection.id}`);
    },

    handleIndustryRemoved(selection) {
      const industries = this._filters[FILTERS.INDUSTRIES];
      const industriesArray = industries.split(',');

      industriesArray.splice(industriesArray.indexOf(selection.id), 1);

      const key = `_filters.${FILTERS.INDUSTRIES}`;
      this.set(key, industriesArray.join(','));

      if (!industriesArray.length) {
        this.set(key, null);
      }
    },

    handleFeaturedClicked() {
      this.set('featuredOnly', !this.featuredOnly);
      // If toggle to false, remove filter
      if (!this.featuredOnly) {
        this.set(`_filters.${FILTERS.FEATURED}`, null);
      } else {
        this.set(`_filters.${FILTERS.FEATURED}`, this.featuredOnly);
      }
    },

    handleUntaggedClicked() {
      this.set('untaggedOnly', !this.untaggedOnly);

      // If toggle to false, remove filter
      if (!this.untaggedOnly) {
        this.set(`_filters.${FILTERS.UNTAGGED}`, null);
      } else {
        this.setProperties({
          [`_filters.${FILTERS.FEATURED}`]: null,
          [`_filters.${FILTERS.SUBJECTS}`]: null,
          [`_filters.${FILTERS.MEDIUMS}`]: null,
          [`_filters.${FILTERS.INDUSTRIES}`]: null,
          [`_filters.${FILTERS.UNTAGGED}`]: this.untaggedOnly,
          featuredOnly: false,
        });
      }
    },

    handleSearch() {
      this.get('onSetFilters')(this.get('_filters'));
    },

    handleReset() {
      this.set('_filters', EmberObject.create(FILTERS_OBJECT));
      this.get('onSetFilters')(this.get('_filters'));

      // Reset the helper props that are associated with actual filters.
      this.set('servicesMatchAll', false);
      this.set('videoTopicsMatchAll', false);
      this.set('selectFreelancersOnly', false);
      this.set('featuredOnly', false);
      this.set('untaggedOnly', false);
    },

    handlePageChange(increment) {
      this.get('onPageChange')(increment);
    },

    handleAddToast(message) {
      this.get('onAddToast')(message);
    },

    handleGetThumbnail(link) {
      return this.get('onGetThumbnail')(link);
    },

    handleOpenVideoPlayer(projectDigest) {
      this.get('onOpenVideoPlayer')(projectDigest);
    },
  },
});
